import {useState} from 'react';
import {Button, DrawerProps, Group, Select, Stack, TextInput} from '@mantine/core';
import {
    MdCancel,
    MdCheck,
    MdClear,
} from 'react-icons/md';

import {useSyntheticUsersFilter} from '../hooks/useSyntheticUsersFilter';
import {
    SyntheticUserLifecycleStage,
    SyntheticUsersFilter,
    SyntheticUserState,
} from '../types';
import {PlatformDrawer} from '@flexinet/ui-components';

// Only the three fields SyntheticUsersFilterInput accepts.
const STATE_OPTIONS = [
    {
        value: '',
        label: 'All states'
    },
    ...Object.values(SyntheticUserState)
             .map(value => ({
                 value,
                 label: value
             })),
];

const LIFECYCLE_OPTIONS = [
    {
        value: '',
        label: 'All lifecycle stages'
    },
    ...Object.values(SyntheticUserLifecycleStage)
             .map(value => ({
                 value,
                 label: value
             })),
];

export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = useSyntheticUsersFilter();

    const [draft, setDraft] = useState<SyntheticUsersFilter>(filters);

    const setField = (field: keyof SyntheticUsersFilter) => (value: string | null) =>
        setDraft(current => ({
            ...current,
            [field]: value ?? ''
        }));

    return (
        <PlatformDrawer
            opened={opened}
            onClose={onClose}
            title='Filter Synthetic Users'
            size='md'
            {...rest}
        >
            <Stack gap='md' my='md'>
                <Select
                    label='State'
                    // Tombstones are included by default and are selectable here, never
                    // filtered out silently (FNP-848).
                    description='Tombstoned users are shown by default — they are the record that a synthetic user existed.'
                    data={STATE_OPTIONS}
                    value={draft.state ?? ''}
                    onChange={setField('state')}
                />
                <Select
                    label='Lifecycle Stage'
                    data={LIFECYCLE_OPTIONS}
                    value={draft.lifecycleStage ?? ''}
                    onChange={setField('lifecycleStage')}
                />
                <TextInput
                    label='Persona ID'
                    value={draft.personaId ?? ''}
                    onChange={(event) => setField('personaId')(event.currentTarget.value)}
                />
            </Stack>

            <Group justify='space-between' mt='xl'>
                <Button
                    variant='outline'
                    leftSection={<MdCancel/>}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Group gap='xs'>
                    <Button
                        variant='outline'
                        leftSection={<MdClear/>}
                        onClick={() => {
                            setDraft({});
                            handleClearFilters();
                        }}
                    >
                        Clear
                    </Button>
                    <Button
                        leftSection={<MdCheck/>}
                        onClick={() => {
                            handleSetFilters(draft);
                            onClose();
                        }}
                    >
                        Apply
                    </Button>
                </Group>
            </Group>
        </PlatformDrawer>
    );
};
